<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import MetricList from '../parts/MetricList.vue';
import QuoteBlock from '../parts/QuoteBlock.vue';
import { formatDateTime } from '../helpers';

const props = defineProps({
  source: { type: Object, required: true },
});

const { t, locale } = useI18n();

const post = computed(() => props.source.post || {});
const parent = computed(() => props.source.parent_comment || null);

const POST_TYPE_ICONS = {
  post: 'i-lucide-image',
  reel: 'i-lucide-clapperboard',
  video: 'i-lucide-play',
  short: 'i-lucide-smartphone',
  clip: 'i-lucide-clapperboard',
  topic: 'i-lucide-newspaper',
  tweet: 'i-lucide-message-square',
  channel_post: 'i-lucide-megaphone',
  tagged_post: 'i-lucide-at-sign',
};

const typeIcon = computed(
  () => POST_TYPE_ICONS[post.value.type] || 'i-lucide-file-text'
);

const typeLabel = computed(() => {
  if (!post.value.type) return t('MULTICHAT.SOURCE.POST_TYPES.POST');
  const key = `MULTICHAT.SOURCE.POST_TYPES.${post.value.type.toUpperCase()}`;
  return t(key) === key ? post.value.type : t(key);
});

const isVideo = computed(() =>
  ['reel', 'video', 'short', 'clip'].includes(post.value.type)
);

const metrics = computed(() => [
  {
    key: 'views',
    icon: 'i-lucide-eye',
    value: post.value.views,
    label: t('MULTICHAT.SOURCE.METRICS.VIEWS'),
  },
  {
    key: 'likes',
    icon: 'i-lucide-heart',
    value: post.value.likes,
    label: t('MULTICHAT.SOURCE.METRICS.LIKES'),
  },
  {
    key: 'comments',
    icon: 'i-lucide-message-circle',
    value: post.value.comments,
    label: t('MULTICHAT.SOURCE.METRICS.COMMENTS'),
  },
  {
    key: 'saves',
    icon: 'i-lucide-bookmark',
    value: post.value.saves,
    label: t('MULTICHAT.SOURCE.METRICS.SAVES'),
  },
  {
    key: 'shares',
    icon: 'i-lucide-repeat-2',
    value: post.value.shares,
    label: t('MULTICHAT.SOURCE.METRICS.SHARES'),
  },
]);

const postUrl = computed(() => post.value.url || props.source.url);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex gap-3 p-3 rounded-lg bg-n-alpha-1 border border-n-weak">
      <div
        class="relative flex-shrink-0 overflow-hidden rounded-lg bg-n-alpha-2 border border-n-weak grid place-items-center"
        :class="isVideo ? 'w-14 h-20' : 'size-16'"
      >
        <img
          v-if="post.thumbnail_url"
          :src="post.thumbnail_url"
          :alt="post.title || typeLabel"
          class="object-cover size-full"
        />
        <span v-else class="size-6 text-n-slate-9" :class="typeIcon" />
        <span
          v-if="post.duration"
          class="absolute bottom-1 ltr:right-1 rtl:left-1 px-1 rounded bg-n-alpha-black1 text-white text-[10px] font-semibold tabular-nums"
        >
          {{ post.duration }}
        </span>
      </div>
      <div class="flex flex-col min-w-0 gap-1.5 flex-1">
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px]">
          <span
            class="inline-flex items-center gap-1 h-5 px-1.5 rounded-md bg-n-alpha-2 font-semibold text-n-slate-12"
          >
            <span class="size-3" :class="typeIcon" />
            {{ typeLabel }}
          </span>
          <a
            v-if="post.author && post.author_url"
            :href="post.author_url"
            target="_blank"
            rel="noopener noreferrer"
            class="font-medium text-n-slate-11 hover:underline"
          >
            {{ post.author }}
          </a>
          <span v-else-if="post.author" class="font-medium text-n-slate-11">
            {{ post.author }}
          </span>
          <span v-if="post.published_at" class="text-n-slate-10">
            {{ formatDateTime(post.published_at, locale) }}
          </span>
          <span class="flex-1" />
          <a
            v-if="postUrl"
            :href="postUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 font-medium text-n-blue-11 hover:underline"
          >
            {{ t('MULTICHAT.SOURCE.OPEN_POST') }}
            <span class="i-lucide-external-link size-3" />
          </a>
        </div>
        <span
          v-if="post.title"
          class="text-[13px] font-semibold leading-5 text-n-slate-12 line-clamp-2"
        >
          {{ post.title }}
        </span>
        <p
          v-if="post.text"
          class="m-0 text-xs leading-[18px] text-n-slate-11 whitespace-pre-line line-clamp-3"
        >
          {{ post.text }}
        </p>
        <MetricList :items="metrics" />
      </div>
    </div>
    <QuoteBlock
      v-if="parent"
      icon="i-lucide-corner-down-right"
      :author="parent.author"
      :meta="
        [
          t('MULTICHAT.SOURCE.PARENT_COMMENT'),
          formatDateTime(parent.published_at, locale),
        ]
          .filter(Boolean)
          .join(' · ')
      "
      :text="parent.text"
    />
  </div>
</template>
